import {
	GridApi,
	GridColDef,
	GridColType,
	GridComparatorFn,
	gridDateComparator,
	gridNumberComparator,
	GridRowId,
	GridSortCellParams,
	GridSortDirection,
	gridStringOrNumberComparator,
	GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { TWrapperFilterFunction } from "../mui/filters";
import { or } from "../sonar";

const getComparatorValue = <T extends GridValidRowModel>(
	swapCounterparts: Map<string, string>,
	id: string,
	value: unknown,
	api: GridApi,
	colDef: GridColDef<T>
) => {
	const { field, valueGetter } = colDef;

	const counterpartId = swapCounterparts.get(id);

	if (!counterpartId) {
		return {
			value,
			id,
		};
	}

	const counterpartRow = api.state.rows.dataRowIdToModelLookup[counterpartId] as T;
	let counterpartValue = counterpartRow[field as keyof T];

	if (valueGetter) {
		counterpartValue =
			valueGetter(counterpartRow[field as keyof T] as never, counterpartRow, colDef, { current: api }) ?? counterpartValue;
	}

	return {
		value: counterpartValue,
		id: counterpartId,
	};
};

// type CustomComparatorFnBuilder = <T extends { _id: GridRowId }>(
// 	direction: GridSortDirection,
// 	colDef: GridColDef<T>,
// 	matchingRowIds: Map<string, string>,
// 	sameRowComparatorFn?: SameRowComparatorFn<T>
// ) => GridComparatorFn;

type SameRowComparatorFn<T extends { _id: GridRowId } = { _id: GridRowId }, V = unknown> = (
	v1: V,
	v2: V,
	row1: T,
	row2: T,
	colDef: GridColDef<T>,
	api: GridApi
) => number;

const BASE_ROW_NODE = {
	depth: 0,
	type: "leaf",
	parent: "",
	groupingKey: null,
} as const;

const defaultSameRowComparatorFn = <T extends { _id: GridRowId }>(
	v1: unknown,
	v2: unknown,
	row1: T,
	row2: T,
	{ field, type }: GridColDef<T>,
	api: GridApi
): number => {
	const rowNode1 = {
		...BASE_ROW_NODE,
		id: row1._id,
	};
	const rowNode2 = {
		...BASE_ROW_NODE,
		id: row2._id,
	};

	return defaultComparatorFn(
		v1,
		v2,
		{ id: row1._id, value: v1, rowNode: rowNode1, field, api },
		{ id: row2._id, value: v2, rowNode: rowNode2, field, api },
		type
	);
};
/**
 *
 * This function builds a custom comparator function that will be used to sort the rows, while keeping multiple rows together.
 *
 * @param colDef {GridColDef}
 * @param matchingRowIds {Map<string, string>} - A map of row ids that should be kept together. The key is the row id and the value is the row id of the row that will be followed.
 * @param sameRowComparatorFn {SameRowComparatorFn} - A custom comparator function that will be used to compare and sort the linked rows,
 * by default it will use the defaultComparatorFn.
 * @returns
 */
export const linkedRowsComparatorFnBuilder = <T extends { _id: GridRowId }>(
	direction: GridSortDirection,
	colDef: GridColDef<T>,
	matchingRowIds: Map<string, string>,
	sameRowComparatorFn?: SameRowComparatorFn<T>
) => {
	return ((v1, v2, cellParams1, cellParams2) => {
		const { id: id1, api } = cellParams1;
		const { id: id2 } = cellParams2;

		let newId1 = id1;
		let newV1 = v1;

		if (matchingRowIds.get(String(id1))) {
			const { id, value } = getComparatorValue(matchingRowIds, String(id1), v1, api, colDef);
			newId1 = id;
			newV1 = value;
		}

		let newId2 = id2;
		let newV2 = v2;

		if (matchingRowIds.get(String(id2))) {
			const { id, value } = getComparatorValue(matchingRowIds, String(id2), v2, api, colDef);
			newId2 = id;
			newV2 = value;
		}

		const multiplier = direction === "asc" ? 1 : -1;

		if (newId1 === newId2) {
			const row1 = api.state.rows.dataRowIdToModelLookup[newId1] as { _id: GridRowId };
			const row2 = api.state.rows.dataRowIdToModelLookup[newId2] as { _id: GridRowId };
			const args = [newV1, newV2, row1 as T, row2 as T, colDef, api] as const;

			return sameRowComparatorFn?.(...args) ?? multiplier * defaultSameRowComparatorFn(...args);
		}

		return (
			multiplier *
			defaultComparatorFn(
				newV1,
				newV2,
				{ ...cellParams1, id: newId1, value: newV1 },
				{ ...cellParams2, id: newId2, value: newV2 },
				colDef.type
			)
		);
	}) as GridComparatorFn;
};

type DefaultComparatorFn = <V = unknown>(
	v1: V,
	v2: V,
	cellParams1: GridSortCellParams<V>,
	cellParams2: GridSortCellParams<V>,
	colType?: GridColType
) => number;

const defaultComparatorFn: DefaultComparatorFn = (v1, v2, cellParams1, cellParams2, colType = "string") => {
	if ((["date", "dateTime"] as GridColType[]).includes(colType)) {
		return gridDateComparator(v1, v2, cellParams1, cellParams2);
	}

	if ((["custom", "number"] as GridColType[]).includes(colType)) {
		return gridNumberComparator(v1, v2, cellParams1, cellParams2);
	}

	if ((["string", "singleSelect"] as GridColType[]).includes(colType)) {
		return gridStringOrNumberComparator(v1, v2, cellParams1, cellParams2);
	}

	return 0;
};

export function buildLinkedRowFilterFn<T extends GridValidRowModel>(links: Map<string, string>): TWrapperFilterFunction<T> {
	return (value, row, colDef, apiRef, originalFn) => {
		const linkedId = links.get(row._id);
		if (linkedId) {
			const linkedValue = apiRef.current.getCellValue(linkedId, colDef.field);
			const linkedRow = apiRef.current.getRow(linkedId);
			return or(originalFn(linkedValue, linkedRow, colDef, apiRef), originalFn(value, row, colDef, apiRef));
		}
		return originalFn(value, row, colDef, apiRef);
	};
}
